import React, { useEffect } from 'react';
import EventBox from 'components/EventBox';
import EventBoxSmall from 'components/EventBoxSmall';
import Card from 'components/Card';
import CardContent from 'components/CardContent';
import SectionTitle from 'components/SectionTitle';
import HorizontalScroller from 'components/HorizontalScroller';
import useFeature from 'hooks/useFeature';
import { useDispatch, useSelector } from 'react-redux';
import { calendarActions, calendarSelectors } from 'redux/calendar';
import NavLink from 'components/NavLink';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const EventBoxMapper = ({ id, size }) => {

  const event = useSelector(calendarSelectors.getEventSelector)(id);

  if (!event) {
    return null;
  }

  const eventProps = {
    id: event.id,
    name: event.name,
    cover: event.coverEffective,
    startAt: event.startAt,
    endAt: event.endAt,
    allDay: event.allDay,
    hasTargets: event.hasTargets,
    targetUsersCount: event.targetUsersCount,
  };

  if (size === 'small') {
    return (
      <Card style={{ minWidth: 300, maxWidth: '100%' }}>
        <CardContent>
          <EventBoxSmall {...eventProps} />
        </CardContent>
      </Card>
    );
  }

  return <EventBox {...eventProps} />;
};

EventBoxMapper.propTypes = {
  id: PropTypes.any.isRequired,
  size: PropTypes.oneOf(['large', 'small']),
};

const defaultSeeMore = {
  component: NavLink,
  to: '/calendar',
};

const CalendarWidget = ({ seeMore = defaultSeeMore, size = 'large', ...args }) => {

  const dispatch = useDispatch();
  const { t } = useTranslation(['calendar']);
  const { enabled } = useFeature('calendar');

  const { eventIds } = useSelector(state => {
    const upcoming = calendarSelectors.getUpcomingEvents(state);
    return {
      ...upcoming,
      eventIds: upcoming.eventIds.slice().splice(0, 3),
    };
  });

  useEffect(() => {
    if (enabled) {
      dispatch(calendarActions.getEvents({ status: 'upcoming' }));
    }
  }, [dispatch, enabled]);

  if (!eventIds || eventIds.length === 0 || !enabled) {
    return null;
  }

  return (
    <div {...args}>
      <SectionTitle seeMore={seeMore}>
        {t('calendarWidget.title')}
      </SectionTitle>
      <HorizontalScroller>
        {eventIds.splice(0, 3).map(id => <EventBoxMapper key={id} id={id} size={size} />)}
      </HorizontalScroller>
    </div>
  );
};

CalendarWidget.propTypes = {
  seeMore: PropTypes.object,
  size: PropTypes.oneOf(['large', 'small']),
};

export default CalendarWidget;
